import React, {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import _, { Dictionary, isEmpty, isEqual, kebabCase, keys, last, map, values, keyBy, some } from 'lodash';
import { connect } from 'react-redux';
import {
  TaxBill,
  Assessor,
  ReduxState,
  State,
} from '../../interface';
import InitTable, { Query } from '../Table/Table';
import Loader, { LoaderIndicator } from '../Loader/Loader';
import { InputType } from '../Table/components/Editor/Editor';
import { TaxBillStatus, AssessmentNoticeStatus, RETURNS_STATUSES, TOOLTIP_SECTIONS } from '../../enums';
import useSelectedSystemTaxYear from '../../hook/useSelectedSystemTaxYear';
import style from './TaxBillsTable.module.scss';
import { getAllTaxBills, getAssessorsByAccount, getCompanies, getLegalEntities, getProperties, bulkUpdateTaxBills, getErrorMessage } from '../../services';
import classNames from 'classnames';
import useUserProfile from '../../hook/useUserProfile';
import TableMenu, { MenuItem } from '../TableMenu/TableMenu';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { formatDate } from '../../utils/stringUtil';
import FieldDate from '../FieldDate/FieldDate';
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper';
import { ToastMessageRefType } from '../ToastMessage/ToastMessage';
import { formatCurrency } from '../../utils/formatNumber';

const { Table, Column, TopBar } = InitTable<TaxBill>();

interface Props {
  states: State[];
  includeReturnsWithNoAssets: boolean;
  toastRef: MutableRefObject<ToastMessageRefType | null>;
}

const RETURN_UNFILED_STATUSES = RETURNS_STATUSES.filter(status => status !== 'Filed');

function TaxBillsTable(props: Props) {
  const {
    states,
    includeReturnsWithNoAssets,
    toastRef
  } = props;
  const navigate = useNavigate();
  const [taxBills, setTaxBills] = useState<TaxBill[]>([]);
  const [billsLength, setBillsLength] = useState<number>(0);
  const [options, setOptions] = useState<Dictionary<unknown[]>>({});
  const [fetchingData, setFetchingData] = useState(false);
  const [fetchingAllAssessors, setFetchingAllAssessors] = useState(false);
  const [fetchingCompanies, setFetchingCompanies] = useState(false);
  const [fetchingEntities, setFetchingEntities] = useState(false);
  const [fetchingProperties, setFetchingProperties] = useState(false);
  const [assessors, setAssessors] = useState<Assessor[]>([]);
  const selectedSystemTaxYear = useSelectedSystemTaxYear();
  const lastQueryRef = useRef<Dictionary<unknown>>(undefined);
  const userProfile = useUserProfile();
  const accountId = userProfile?.userAccountId;
  const [query, setQuery] = useState<Query>({});

  useEffect(() => {
    if (accountId) {
      setFetchingAllAssessors(true);
      getAssessorsByAccount({ userAccountId: accountId.toString() })
        .then((d) => {
          setAssessors(
            d.data.items.map((item: Assessor) => ({
              id: item.id,
              label: item.name,
              name: item.name,
              value: item.id,
            })),
          );
          setFetchingAllAssessors(false);
        })
        .catch(() => {
          setFetchingAllAssessors(false);
        });
    }
  }, [accountId]);

  const responseToOptions = (res: AxiosResponse) =>
    map(res.data.items, (x: { id: unknown; name: string; number: string }) => ({
      value: x.id,
      label: x.name ? x.name : '-',
      number: x.number,
    }));
  const addResponseOptions = (key: string, res: AxiosResponse) =>
    setOptions((current) => ({
      ...current,
      [key]: responseToOptions(res),
    }));

  useEffect(() => {
    setFetchingCompanies(true);
    getCompanies()
      .then((res) => {
        addResponseOptions('companyIds', res);
        setFetchingCompanies(false);
      })
      .catch(() => {
        setFetchingCompanies(false);
      });

    setFetchingEntities(true);
    getLegalEntities()
      .then((res) => {
        addResponseOptions('legalEntityIds', res);
        setFetchingEntities(false);
      })
      .catch(() => {
        setFetchingEntities(false);
      });

    setFetchingProperties(true);
    getProperties()
      .then((res) => {
        addResponseOptions('propertyIds', res);
        setFetchingProperties(false);
      })
      .catch(() => {
        setFetchingProperties(false);
      });
  }, []);

  useEffect(() => {
    fetchData(_.noop);
  }, [query, selectedSystemTaxYear.taxYear, includeReturnsWithNoAssets]);

  const fetchData = (onComplete: () => void) => {
    let returnStatus = '';
    if (query?.filter?.['assessmentNotice.return.status']) {
      returnStatus = (query?.filter?.['assessmentNotice.return.status']) === 'Filed' ?
        'Filed' :
        RETURN_UNFILED_STATUSES.join(',');
    }
    const formattedQuery = {
      page: query?.pagination?.page || 1,
      perPage: query?.pagination?.pageSize ?? 10,
      sortBy: last(keys(query.order)),
      sortDirection: last(values(query.order)),
      text: query?.search,
      taxYear: selectedSystemTaxYear.taxYear ?? '',
      billStatuses: (query?.filter?.taxBillStatus as string[])?.join(','),
      noticeStatuses: (query?.filter?.['assessmentNotice.noticeStatus'] as string[])?.join(','),
      returnStatuses: returnStatus,
      companyIds: (query?.filter?.companyIds as number[])?.join(','),
      legalEntityIds: (query?.filter?.legalEntityIds as number[])?.join(','),
      propertyIds: (query?.filter?.propertyIds as string[])?.join(','),
      stateIds: (query?.filter?.stateIds as number[])?.join(','),
      assessorIds: (query?.filter?.assessorIds as number[])?.join(','),
      paymentDeadlineRange: query?.filter?.['paymentDeadline'] as string[],
      includeReturnsWithNoAssets,
    };

    if (!isEqual(formattedQuery, lastQueryRef.current)) {
      lastQueryRef.current = formattedQuery;
      setFetchingData(true);
      getAllTaxBills(formattedQuery)
        .then((res) => {
          setTaxBills(res.data.items);
          setBillsLength(res.data.total);
          setSelected([]);
          setFetchingData(false);
          onComplete();
        })
        .catch(() => {
          setFetchingData(false);
          onComplete();
        });
    } else {
      onComplete();
    }
  };
  const formatNameAndNumber = (name?: string, number?: string) => {
    return _.compact([name, number && `${number}`]).join(' | ');
  };

  const getMenuItems = ({ taxBillStatus, id, assessmentNoticeId }: TaxBill): MenuItem[][] => {

    const actions = [
      taxBillStatus === TaxBillStatus.Missing && {
        'Enter Bill': () => {
          navigate(`/taxes/editTaxBill/${id}`);
        },
      },
      [TaxBillStatus.Received, TaxBillStatus.Paid].includes(taxBillStatus) && {
        'Edit Bill': () => {
          navigate(`/taxes/editTaxBill/${id}`);
        }
      },
      [TaxBillStatus.Received, TaxBillStatus.Paid].includes(taxBillStatus) && {
        'Enter New Bill': () => {
          navigate(`/taxes/taxBillSetup?assessmentNoticeId=${assessmentNoticeId}`);
        },
      },
    ];

    return [
      _.map(_.assign({}, ..._.compact(actions)), (onClick, label) => {
        return {
          variant: 'default',
          tooltip: {
            section: TOOLTIP_SECTIONS.TableRowAction,
            keyword: label,
          },
          label,
          onClick,
        } as MenuItem;
      })
    ].filter((arr) => !isEmpty(arr));
  };

  // Bulk actions
  const [selected, setSelected] = useState<TaxBill[]>([]);
  const selectedIds = useMemo(
    () => (_.compact(selected).length ? selected.map((item) => item?.id) : []),
    [selected],
  );
  const canSaveSelected = useMemo(
    () =>
      selected.length > 0 &&
      !some(selected, (x) =>
        [
          TaxBillStatus.Paid,
        ].includes(x?.taxBillStatus as TaxBillStatus),
      ),
    [selected],
  );
  const [selectedItemsPaymentDeadline, setSelectedItemsPaymentDeadline] = useState<string | null>(null);
  const [savingTaxBills, setSavingTaxBills] = useState(false);

  const bulkSaveTaxBills = () => {
    setSavingTaxBills(true);
    bulkUpdateTaxBills(selectedIds.join(','), selectedItemsPaymentDeadline)
      .then(() => {
        toastRef.current?.showSuccessToast('Tax Bills saved successfully');
        setSelected([]);
        setSelectedItemsPaymentDeadline(null);
        lastQueryRef.current = undefined;
        fetchData(_.noop);
        setSavingTaxBills(false);
      })
      .catch((e) => {
        toastRef.current?.showErrorToast(getErrorMessage(e));
        setSavingTaxBills(false);
      });
  };

  useEffect(() => {
    const byId = keyBy(taxBills, (x) => x.id);
    setSelected(selected.filter(x => x.id && byId[x.id]));
  }, [taxBills]);

  return (
    <div className={style['table-layout']}>
      <Table
        id={'taxBills'}
        rows={taxBills}
        onQueryChanged={(x) => setQuery(x)}
        paginate
        sortable
        selectable={{ selected, onChange: setSelected }}
        searchable='Search accounts...'
        totalRows={billsLength}
        dynamicColumns
        loading={fetchingData}
      >
        <TopBar>
          {selected.length > 0 && (
            <div className={style['selected-items-action']}>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.Common}
                tooltipKey='Update Payment Deadlines'
              >
                <label>Update Payment Deadlines to</label>
              </TooltipWrapper>
              <FieldDate
                classnames={style['field-select']}
                labelText=''
                primaryFormat='MM/DD/YYYY'
                selectValue={selectedItemsPaymentDeadline ?? ''}
                onSelect={(value) => {
                  setSelectedItemsPaymentDeadline(value ?? null);
                }}
              />
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Save'
              >
                <button
                  className='primary'
                  disabled={savingTaxBills || !canSaveSelected}
                  onClick={() => bulkSaveTaxBills()}
                >
                  Save ({selected.length})
                  {savingTaxBills ? (
                    <LoaderIndicator
                      className='button-loading'
                      loading={true}
                    />
                  ) : null}
                </button>
              </TooltipWrapper>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Cancel'
              >
                <button
                  className='secondary'
                  onClick={() => {
                    setSelected([]);
                    setSelectedItemsPaymentDeadline(null);
                  }}
                >
                  Cancel
                </button>
              </TooltipWrapper>
            </div>
          )}
        </TopBar>
        <Column
          label='Bill Status'
          prop='taxBillStatus'
          filterable
          editor={{
            type: InputType.Select,
            options: _.map(TaxBillStatus, (status) => status),
            multi: true,
          }}
        >
          {(status: string) => (
            <span className={classNames('badge-status', kebabCase(status))}>
              {status}
            </span>
          )}
        </Column>

        <Column
          label='Notice Status'
          prop='assessmentNotice.noticeStatus'
          filterable
          hidden
          editor={{
            type: InputType.Select,
            options: _.map(AssessmentNoticeStatus, (status) => status),
            multi: true,
          }}
        >
          {(status: string) => (
            <span className={classNames('badge-status', kebabCase(status))}>
              {status}
            </span>
          )}
        </Column>

        <Column
          label='Return Status'
          prop='assessmentNotice.return.status'
          filterable
          hidden
          editor={{
            type: InputType.Select,
            options: ['Filed', 'Unfiled'],
            multi: false,
          }}
        >
          {(status: string) => {
            const reducedStatus = status === 'Filed' ? 'Filed' : 'Unfiled';
            return (
              <span className={classNames('badge-status', kebabCase(reducedStatus))}>
                {reducedStatus}
              </span>
            )
          }}
        </Column>

        <Column
          label='State'
          id='assessmentNotice.return.form.state.abbreviation'
          prop='assessmentNotice.return.form.state.abbreviation'
          accessor={(bill) =>
            bill.assessmentNotice.return?.form?.state?.abbreviation ?? ''
          }
        />

        <Column
          id='assessmentNotice.return.assessorAccount.assessor.name'
          label='Assessor'
          accessor={(bill) =>
            bill.assessmentNotice.return?.status === 'Filed' ?
              bill.assessmentNotice.return?.assessorAccountData?.assessor?.name ?? '' :
              bill.assessmentNotice.return?.assessorAccount?.assessor?.name ?? ''
          }
        />

        <Column
          id='assessmentNotice.return.assessorAccount.number'
          label='Assessor account number'
          accessor={(bill) =>
            bill.assessmentNotice.return?.status === 'Filed' ?
              bill.assessmentNotice.return?.assessorAccountData?.number ?? '' :
              bill.assessmentNotice.return?.assessorAccount?.number ?? ''
          }
        />

        <Column
          id='assessmentNotice.finalAssessedValue'
          label='Final Assessed Value'
          cssClasses='text-align-right'
        >
          {(bill: TaxBill) => {
            const finalAssessedValue=  (bill?.assessmentNotice?.noticeAssessedValue
              && bill?.assessmentNotice.noticeStatus===AssessmentNoticeStatus.Accepted)?
              bill?.assessmentNotice?.noticeAssessedValue:bill?.assessmentNotice?.finalAssessedValue;
            
             return Math.round(finalAssessedValue) || '';
          }}
        </Column>

        <Column
          id='assessmentNotice.return.totalAssessedValue'
          label='Reported Assessed Value'
          cssClasses='text-align-right'
          hidden
        >
          {(bill: TaxBill) => (
            <div>
              {Math.round(bill?.assessmentNotice?.return?.totalAssessedValue) || ''}
            </div>
          )}
        </Column>
        <Column
          id='assessmentNotice.noticeAssessedValue'
          label='Notice Assessed Value'
          cssClasses='text-align-right'
          hidden
        >
          {(bill: TaxBill) => (
            <div>
              {Math.round(bill?.assessmentNotice?.noticeAssessedValue) || ''}
            </div>
          )}
        </Column>

        <Column
          id='collector'
          label='Collector'
          accessor={(bill) => bill.collector ?? ''}
        />

        <Column
          id='collectorAccountNumber'
          label='Collector Account Number'
          accessor={(bill) => bill.collectorAccountNumber ?? ''}
        />

        <Column
          id='paymentDeadline'
          prop='paymentDeadline'
          label='Payment Deadline'
          accessor={(bill: TaxBill) => bill.paymentDeadline || ''}
          filterable
          hidden
          editor={{
            type: InputType.DateRange,
            label: ['Payment deadline From Date', 'Payment deadline To Date'],
          }}
          cssClasses='text-align-right'
        >
          {formatDate}
        </Column>

        <Column
          id='totalTaxAmount'
          label='Total Tax Amount'
          cssClasses='text-align-right'
          hidden
        >
          {(bill: TaxBill) => (
            <div>
               {formatCurrency(bill?.totalTaxAmount,true)}
            </div>
          )}
        </Column>

        <Column
          id='assessmentNotice.return.assessorAccount.property.company'
          label='Company Name & Number'
          hidden
        >
          {(bill: TaxBill) => {
            const company = bill.assessmentNotice.return?.status === 'Filed' ?
              bill.assessmentNotice.return?.assessorAccountData?.property?.company :
              bill.assessmentNotice.return?.assessorAccount?.property?.company;
            return formatNameAndNumber(company?.name, company?.number) || '-';
          }}
        </Column>

        <Column
          id='assessmentNotice.return.assessorAccount.property.legalEntity'
          label='Legal Entity Name & Number'
          hidden
        >
          {(bill: TaxBill) => {
            const legalEntity = bill.assessmentNotice.return?.status === 'Filed' ?
              bill.assessmentNotice.return?.assessorAccountData?.property?.legalEntity :
              bill.assessmentNotice.return?.assessorAccount?.property?.legalEntity;
            return formatNameAndNumber(legalEntity?.name, legalEntity?.number)
          }}
        </Column>

        <Column
          id='assessmentNotice.return.assessorAccount.property'
          label='Property Name & Number'
          hidden
        >
          {(bill: TaxBill) => {
            const property = bill.assessmentNotice.return?.status === 'Filed' ?
              bill.assessmentNotice.return?.assessorAccountData?.property :
              bill.assessmentNotice.return?.assessorAccount?.property;
            return formatNameAndNumber(property?.name, property?.number)
          }}
        </Column>

        <Column id='actions' label='Actions' sortable={false}>
          {(item: TaxBill) => (
            <div className={style['actions-column-cell']}>
              <TableMenu
                toggle={
                  <button type='button'>
                    <i className={style['icon-3-dots']} />
                  </button>
                }
                menuItems={getMenuItems(item)}
              />
            </div>
          )}
        </Column>

        <Column
          id='companyIds'
          hidden='always'
          label={['Company', 'Select one or more companies']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['companyIds'],
            getLabel: (x) => `${_.get(x, 'label')} | ${_.get(x, 'number')}`,
            multi: true,
          }}
        />

        <Column
          id='legalEntityIds'
          hidden='always'
          label={['Legal Entity', 'Select one or more legal entities']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['legalEntityIds'],
            getLabel: (x) => `${_.get(x, 'label')} | ${_.get(x, 'number')}`,
            multi: true,
          }}
        />

        <Column
          id='propertyIds'
          hidden='always'
          label={['Property', 'Select one or more properties']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['propertyIds'],
            getLabel: (x) => `${_.get(x, 'label')} | ${_.get(x, 'number')}`,
            multi: true,
          }}
        />

        <Column
          id='stateIds'
          hidden='always'
          label={['State', 'Select one or more states']}
          filterable
          editor={{
            type: InputType.Select,
            options: states,
            getLabel: (x) => (x as State).abbreviation,
            getValue: (x) => (x as State).id,
            multi: true,
          }}
        />

        <Column
          id='assessorIds'
          hidden='always'
          label={['Assessor', 'Select one or more assessor names']}
          filterable
          editor={{
            type: InputType.Select,
            options: assessors,
            multi: true,
          }}
        />

      </Table>

      <Loader isOpen={
        fetchingData
        || fetchingAllAssessors
        || fetchingCompanies
        || fetchingEntities
        || fetchingProperties
        || savingTaxBills
      } />
    </div>
  )
}

const mapStateToProps = (state: ReduxState) => ({
  states: state.states,
});

export default connect(mapStateToProps)(TaxBillsTable); 